import { ScrollReveal, SplitReveal } from '@/components/motion';
import {
  EVENT_FORMATS,
  EVENTS_LEDE,
  EVENTS_WORKSHOP_NOTE,
  STUDENT_PROGRAMMES,
  type StudentProgramme,
} from '@/data/current-students';

/**
 * EventsSocialProgrammes (/current-students — section 2) — "Community, growth
 * and connection."
 *
 * Opens with the recurring EVENT_FORMATS as a row of pill chips, then presents
 * the four STUDENT_PROGRAMMES (Centre for Learning Excellence, Business
 * Incubator, Career Centre, Alumni network) as plain hover-lift feature cards —
 * an icon in a navy chip, name, description and a gold-marker points list —
 * closing with EVENTS_WORKSHOP_NOTE as a small muted aside beneath.
 *
 * Sourcing: every format, programme and point comes from
 * '@/data/current-students' (source: /events-social-programmes/); nothing is
 * invented or embellished beyond the connective microcopy.
 */
export function EventsSocialProgrammes() {
  return (
    <section className="border-t border-border bg-muted/30 py-16 md:py-24">
      <div className="mx-auto w-full max-w-6xl px-6">
        {/* ── Heading + lede ──────────────────────────────────────────── */}
        <header className="max-w-2xl">
          <p className="font-label text-xs tracking-[0.22em] text-brand-gold uppercase">
            Events &amp; social programmes
          </p>
          <SplitReveal
            as="h2"
            by="lines"
            className="mt-3 font-display text-3xl leading-tight text-foreground md:text-4xl"
          >
            Community, growth and connection.
          </SplitReveal>
          <ScrollReveal from="up" delay={0.1}>
            <p className="mt-5 text-base leading-relaxed text-muted-foreground md:text-lg">
              {EVENTS_LEDE}
            </p>
          </ScrollReveal>
        </header>

        {/* ── Event formats — pill row ─────────────────────────────────── */}
        <ScrollReveal stagger={0.06} className="mt-8 flex flex-wrap gap-3">
          {EVENT_FORMATS.map((format) => (
            <span
              key={format}
              className="inline-flex items-center rounded-full border border-brand-gold/30 bg-background px-5 py-2 text-sm font-medium text-brand-navy"
            >
              {format}
            </span>
          ))}
        </ScrollReveal>

        {/* ── Student programmes — feature cards ───────────────────────── */}
        <ScrollReveal
          stagger={0.08}
          className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2"
        >
          {STUDENT_PROGRAMMES.map((programme: StudentProgramme) => (
            <div
              key={programme.name}
              className="h-full rounded-2xl border border-border bg-background p-7 shadow-sm transition-all duration-500 hover:-translate-y-1 hover:border-brand-gold/30 hover:shadow-xl motion-reduce:transition-none motion-reduce:hover:translate-y-0"
            >
              <div className="flex size-12 items-center justify-center rounded-xl bg-brand-navy/5 text-brand-navy">
                <programme.Icon aria-hidden className="size-6" />
              </div>
              <h3 className="mt-5 font-display text-xl leading-tight font-semibold text-foreground">
                {programme.name}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                {programme.description}
              </p>
              <ul className="mt-4 space-y-2">
                {programme.points.map((point) => (
                  <li key={point} className="flex items-start gap-2.5 text-sm leading-relaxed text-muted-foreground">
                    <span aria-hidden className="mt-2 size-1.5 shrink-0 rounded-full bg-brand-gold" />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </ScrollReveal>

        {/* ── Workshop note ────────────────────────────────────────────── */}
        <ScrollReveal from="up" delay={0.1}>
          <p className="mt-6 text-sm leading-relaxed text-muted-foreground">
            {EVENTS_WORKSHOP_NOTE}
          </p>
        </ScrollReveal>
      </div>
    </section>
  );
}
